/**
 * CribbageAI - AI Decision Engine & Heuristics
 * Encapsulates discard selection, Monte Carlo pegging rollouts, Coach Mode EV analysis,
 * and variant-specific heuristics (Stolen Crib, Pick Your Crib, Wild Jokers).
 */

import { Card } from '../models/Card.js';
import { Deck } from '../models/Deck.js';
import { Hand } from '../models/Hand.js';
import { CribbageScorer, combinations } from '../scoring/CribbageScorer.js';
import { RANKS, SUITS, VAL } from '../scoring/ScoringConstants.js';

export class CribbageAI {
  /**
   * Normalizes profile or friendly level string into an AI profile key.
   * @param {string} [prof='shark']
   * @returns {string}
   */
  static normalizeProfile(prof) {
    if (!prof) return 'shark';
    const p = String(prof).trim().toLowerCase();
    if (p === 'expert') return 'shark';
    if (p === 'advanced') return 'turtler';
    if (p === 'intermediate') return 'gambler';
    if (p === 'beginner') return 'rookie';
    return p;
  }

  /**
   * Calculates the exact expected show score across all possible unseen starter cards.
   * @param {Card[]} keep Cards kept in hand
   * @param {Set<string>|Array<string>} [deadIds] Card IDs already known/accounted for
   * @param {boolean} [isCrib=false] Whether this is a crib calculation
   * @param {Card[]} [deck] Deck pool to sample from (defaults to standard 52-card deck)
   * @returns {number} Average score
   */
  static expectedShowExact(keep, deadIds, isCrib = false, deck = null) {
    const pool = deck || Deck.makeDeck(1, false);
    const deadSet = deadIds instanceof Set ? deadIds : new Set(deadIds || []);
    let tot = 0, n = 0;
    for (const s of pool) {
      if (keep.some(c => c.id === s.id)) continue;
      if (deadSet.has(s.id)) continue;
      const scored = CribbageScorer.scoreHand(keep, s, isCrib, { verbose: false });
      tot += (scored && typeof scored.total === 'number') ? scored.total : 0;
      n++;
    }
    return n ? tot / n : 0;
  }

  /**
   * Fast discard heuristic with personality modifiers.
   * @param {Card[]} hand Player's dealt hand
   * @param {boolean} isDealer Whether player is dealer
   * @param {boolean} [lowball=false] Lowball mode flag
   * @param {number} [discardN=2] Number of cards to discard
   * @param {boolean} [toDeck=false] Discarding to deck rather than crib
   * @param {Object} [options={}] Additional context (profile, starter, etc.)
   * @returns {Card[]} Best discard combination
   */
  static chooseDiscardFast(hand, isDealer, lowball = false, discardN = 2, toDeck = false, options = {}) {
    if (!hand || hand.length <= discardN) return (hand || []).slice(0, discardN);
    const rawProf = options.profile || options.aiProfile || 'shark';
    const profile = CribbageAI.normalizeProfile(rawProf);
    const starter = options.starter || null;
    let best = null;
    let bestScore = lowball ? Infinity : -Infinity;
    const combos = combinations(hand, discardN);
    const isFivesCribbage = Boolean(options.optFivesCribbage || (options.settings && options.settings.optFivesCribbage));
    const isSevensAndEightsCribbage = Boolean(options.optSevensAndEightsCribbage || (options.settings && options.settings.optSevensAndEightsCribbage));
    let validCombos = combos;
    if (isFivesCribbage && !toDeck) {
      const fivesInHand = hand.filter(c => ((c.rank !== undefined ? c.rank : c.r) || 0) === 5);
      const reqFives = Math.min(fivesInHand.length, discardN);
      if (reqFives > 0) {
        const filtered = combos.filter(disc => disc.filter(c => ((c.rank !== undefined ? c.rank : c.r) || 0) === 5).length >= reqFives);
        if (filtered.length > 0) validCombos = filtered;
      }
    } else if (isSevensAndEightsCribbage && !toDeck) {
      const sevensAndEightsInHand = hand.filter(c => {
        const r = ((c.rank !== undefined ? c.rank : c.r) || 0);
        return r === 7 || r === 8;
      });
      const reqCount = Math.min(sevensAndEightsInHand.length, discardN);
      if (reqCount > 0) {
        const filtered = combos.filter(disc => disc.filter(c => {
          const r = ((c.rank !== undefined ? c.rank : c.r) || 0);
          return r === 7 || r === 8;
        }).length >= reqCount);
        if (filtered.length > 0) validCombos = filtered;
      }
    }

    for (const disc of validCombos) {
      const ids = new Set(disc.map(c => c.id));
      const keep = hand.filter(c => !ids.has(c.id));
      const scored = CribbageScorer.scoreHand(keep, starter, false, { verbose: false });
      let val = scored ? scored.total : 0;
      if (isNaN(val)) val = 0;

      if (toDeck) {
        val += keep.filter(c => (c.rank !== undefined ? c.rank : c.r) === 5).length * 1.0;
      } else if (profile === 'gambler') {
        // Gambler (Intermediate): loves 5s, flushes, and pairs
        val += keep.filter(c => (c.rank !== undefined ? c.rank : c.r) === 5).length * 2.2;
        const suitCounts = {};
        keep.forEach(c => {
          const s = c.suit || c.s;
          suitCounts[s] = (suitCounts[s] || 0) + 1;
        });
        const vals = Object.values(suitCounts);
        if (vals.length && Math.max(...vals) >= 3) val += 1.8;
      } else if (profile === 'regular') {
        // Regular (Intermediate): steady fundamentals, standard crib penalty
        val += keep.filter(c => (c.rank !== undefined ? c.rank : c.r) === 5).length * 1.0;
        const cribBareScored = CribbageScorer.scoreHand(disc, null, true, { verbose: false });
        const cribBare = cribBareScored ? cribBareScored.total : 0;
        if (isDealer) val += cribBare * 0.35;
        else val -= cribBare * 0.5;
      } else if (profile === 'turtler') {
        // Turtler (Advanced): heavy crib defense
        const cribBareScored = CribbageScorer.scoreHand(disc, null, true, { verbose: false });
        const cribBare = cribBareScored ? cribBareScored.total : 0;
        if (isDealer) val += cribBare * 0.3;
        else val -= cribBare * 1.4; // heavy penalty for giving points away
      } else if (profile === 'maverick') {
        // Maverick (Advanced): tactical peg potential & pace
        val += keep.filter(c => (c.rank !== undefined ? c.rank : c.r) === 5).length * 1.6;
        val += CribbageScorer.pegPotential(keep) * 0.4;
        const cribBareScored = CribbageScorer.scoreHand(disc, null, true, { verbose: false });
        const cribBare = cribBareScored ? cribBareScored.total : 0;
        if (isDealer) val += cribBare * 0.45;
        else val -= cribBare * 0.5;
      } else if (profile === 'rookie' || profile === 'casual') {
        // Beginner (Rookie / Casual): friendly, simple immediate score focus without deep crib penalty
        const cribBareScored = CribbageScorer.scoreHand(disc, null, true, { verbose: false });
        const cribBare = cribBareScored ? cribBareScored.total : 0;
        if (isDealer) val += cribBare * 0.2;
        else val -= cribBare * 0.15;
      } else if (profile === 'master') {
        // Master (Expert): razor sharp tournament precision
        val += keep.filter(c => (c.rank !== undefined ? c.rank : c.r) === 5).length * 1.25;
        const firstSuit = keep[0] ? (keep[0].suit || keep[0].s) : null;
        const suitSame = keep.length && keep.every(c => (c.suit || c.s) === firstSuit);
        if (suitSame && keep.length >= 4) val += 0.9;
        const cribBareScored = CribbageScorer.scoreHand(disc, null, true, { verbose: false });
        const cribBare = cribBareScored ? cribBareScored.total : 0;
        if (isDealer) val += cribBare * 0.45;
        else val -= cribBare * 0.45;
      } else {
        // Shark (Expert): Balanced standard GTO optimal
        val += keep.filter(c => (c.rank !== undefined ? c.rank : c.r) === 5).length * 1.15;
        const firstSuit = keep[0] ? (keep[0].suit || keep[0].s) : null;
        const suitSame = keep.length && keep.every(c => (c.suit || c.s) === firstSuit);
        if (suitSame && keep.length >= 4) val += 0.8;
        const cribBareScored = CribbageScorer.scoreHand(disc, null, true, { verbose: false });
        const cribBare = cribBareScored ? cribBareScored.total : 0;
        if (isDealer) val += cribBare * 0.4;
        else val -= cribBare * 0.3;
      }

      if (lowball) val = -val;
      if (lowball ? val < bestScore : val > bestScore) {
        bestScore = val;
        best = disc;
      }
    }
    return best || validCombos[0] || combos[0] || hand.slice(0, discardN);
  }

  /**
   * Computer discard with tactical personality biases and Monte Carlo simulation.
   */
  static chooseDiscard(hand, isDealer, lowball = false, discardN = 2, toDeck = false, options = {}) {
    if (!hand || hand.length <= discardN) return (hand || []).slice(0, discardN);
    const rawProf = options.profile || options.aiProfile || 'shark';
    const profile = CribbageAI.normalizeProfile(rawProf);
    const combos = combinations(hand, discardN);
    const isFivesCribbage = Boolean(options.optFivesCribbage || (options.settings && options.settings.optFivesCribbage));
    const isSevensAndEightsCribbage = Boolean(options.optSevensAndEightsCribbage || (options.settings && options.settings.optSevensAndEightsCribbage));
    let validCombos = combos;
    if (isFivesCribbage && !toDeck) {
      const fivesInHand = hand.filter(c => ((c.rank !== undefined ? c.rank : c.r) || 0) === 5);
      const reqFives = Math.min(fivesInHand.length, discardN);
      if (reqFives > 0) {
        const filtered = combos.filter(disc => disc.filter(c => ((c.rank !== undefined ? c.rank : c.r) || 0) === 5).length >= reqFives);
        if (filtered.length > 0) validCombos = filtered;
      }
    } else if (isSevensAndEightsCribbage && !toDeck) {
      const sevensAndEightsInHand = hand.filter(c => {
        const r = ((c.rank !== undefined ? c.rank : c.r) || 0);
        return r === 7 || r === 8;
      });
      const reqCount = Math.min(sevensAndEightsInHand.length, discardN);
      if (reqCount > 0) {
        const filtered = combos.filter(disc => disc.filter(c => {
          const r = ((c.rank !== undefined ? c.rank : c.r) || 0);
          return r === 7 || r === 8;
        }).length >= reqCount);
        if (filtered.length > 0) validCombos = filtered;
      }
    }
    const dead = new Set(hand.map(c => c.id));
    const pool = (options.deck || Deck.makeDeck(options.decks || 1, false)).filter(c => !dead.has(c.id));
    const dealN = hand.length;
    const samples = validCombos.length > 30 ? 36 : 64;
    const worlds = [];

    const knownStarter = options.starter || (options.state && options.state.starter) || null;
    for (let t = 0; t < samples; t++) {
      const shuffled = Deck.shuffleArray(pool.slice());
      const oppHand = shuffled.slice(0, dealN);
      const starter = knownStarter || shuffled[dealN];
      const oppDisc = CribbageAI.chooseDiscardFast(oppHand, !isDealer, lowball, discardN, toDeck, { ...options, profile }) || oppHand.slice(0, discardN);
      const oids = new Set(oppDisc.map(c => c.id));
      const oppKeep = oppHand.filter(c => !oids.has(c.id));
      worlds.push({ starter, oppDisc, oppKeep });
    }

    const inBlockedZone = Boolean(options.inBlockedZone);
    const ranked = [];

    for (const disc of validCombos) {
      const ids = new Set(disc.map(c => c.id));
      const keep = hand.filter(c => !ids.has(c.id));
      const rawHandEV = knownStarter 
        ? CribbageScorer.scoreHand(keep, knownStarter, false, { verbose: false }).total 
        : CribbageAI.expectedShowExact(keep, dead, false, pool);
      const handEV = inBlockedZone ? 0 : rawHandEV;
      let cribSum = 0, pegSum = 0;

      for (const w of worlds) {
        const fullCrib = disc.concat(w.oppDisc);
        cribSum += CribbageScorer.scoreHand(fullCrib, w.starter, true, { verbose: false }).total;
        const r = CribbageAI.rolloutPeg(keep.slice(), w.oppKeep.slice(), [], 0, !isDealer, false, options.settings);
        pegSum += r.myPts - r.oppPts;
      }

      const cribEV = (toDeck || (inBlockedZone && isDealer)) ? 0 : (cribSum / worlds.length);
      const pegEV = (pegSum / worlds.length) * (inBlockedZone ? 2.5 : 1.0);
      let val;

      if (toDeck || options.optShotgunCribbage || (options.settings && options.settings.optShotgunCribbage)) {
        val = handEV + pegEV * 0.2 + CribbageScorer.pegPotential(keep) * 0.2;
      } else if (options.target === 31) {
        val = pegEV + CribbageScorer.pegPotential(keep) * 0.2;
      } else {
        if (profile === 'gambler') {
          val = handEV + (isDealer ? cribEV * 0.8 : 0) + pegEV * 0.3;
        } else if (profile === 'regular') {
          val = handEV + (isDealer ? cribEV * 0.75 : -cribEV * 0.75) + pegEV * 0.2;
        } else if (profile === 'turtler') {
          const cribFactor = isDealer ? 0.7 : 1.6;
          val = handEV + (isDealer ? cribEV * cribFactor : -cribEV * cribFactor) + pegEV * 0.25;
        } else if (profile === 'maverick') {
          val = handEV + (isDealer ? cribEV * 0.9 : -cribEV * 0.6) + pegEV * 0.5 + CribbageScorer.pegPotential(keep) * 0.3;
        } else if (profile === 'rookie' || profile === 'casual') {
          val = handEV + (isDealer ? cribEV * 0.35 : -cribEV * 0.2);
        } else if (profile === 'master') {
          val = options.optRockCrib
            ? (isDealer ? handEV - cribEV * 1.05 : handEV + cribEV * 1.05)
            : (isDealer ? handEV + cribEV * 1.05 : handEV - cribEV * 1.05);
          val += pegEV * 1.1 + CribbageScorer.pegPotential(keep) * 0.25;
        } else {
          // Shark (Expert)
          val = options.optRockCrib
            ? (isDealer ? handEV - cribEV : handEV + cribEV)
            : (isDealer ? handEV + cribEV : handEV - cribEV);
          val += pegEV + CribbageScorer.pegPotential(keep) * 0.2;
        }
      }

      const raw = val;
      if (lowball) val = -val;
      ranked.push({ disc, keep, handEV, cribEV, pegEV, val, raw });
    }

    ranked.sort((a, b) => b.val - a.val);
    if ((profile === 'rookie' || profile === 'casual') && ranked.length > 1 && Math.random() < 0.15) {
      if (Math.abs(ranked[0].val - ranked[1].val) <= 1.5) {
        return ranked[1].disc;
      }
    }
    return (ranked[0] && ranked[0].disc) || validCombos[0] || combos[0] || hand.slice(0, discardN);
  }

  /**
   * Fast legal play selector for pegging simulations.
   */
  static fastPlayCard(hand, pile = [], count = 0, settings = {}) {
    let best = null, bestV = -Infinity;
    const opts = [];
    for (const c of hand) {
      const isW = c && (c.isWild || c.wild);
      const r = (c && (c.rank !== undefined ? c.rank : c.r)) || 0;
      if (isW) {
        for (const rank of RANKS) {
          if (count + VAL(rank) <= 31) {
            const assigned = Card.from ? Card.from(c).clone().assignWild(rank, 'S') : { ...c, r: rank, playAs: { r: rank, s: 'S' } };
            opts.push(assigned);
          }
        }
      } else if (count + VAL(r) <= 31) {
        opts.push(c);
      }
    }

    for (const c of opts) {
      const r = (c && (c.rank !== undefined ? c.rank : c.r)) || 0;
      const pts = CribbageScorer.pegTotal(pile.concat([c]), settings);
      const nc = count + VAL(r);
      let v = pts * 3;
      if (nc === 31) v += (settings && settings.opt31sFor5) ? 5 : 2;
      if (nc === 21) v -= 2;
      if (nc === 5 || nc === 10 || nc === 26) v -= 1;
      if (r === 5 && pts === 0) v -= 1;
      if (v > bestV || (v === bestV && best && VAL(r) < VAL(best.rank !== undefined ? best.rank : best.r))) {
        bestV = v;
        best = c;
      }
    }
    return best;
  }

  /**
   * Simulates pegging playout between two hands to estimate net peg points.
   */
  static rolloutPeg(myHand, oppHand, pile = [], count = 0, myTurn = true, lastWasMe = false, settings = {}) {
    myHand = myHand.slice();
    oppHand = oppHand.slice();
    pile = pile.slice();
    let myPts = 0, oppPts = 0;
    let guard = 48;

    while (guard-- && (myHand.length || oppHand.length)) {
      const meCan = myHand.some(c => (c.fits ? c.fits(count) : (count + VAL(c.rank !== undefined ? c.rank : c.r) <= 31)));
      const oppCan = oppHand.some(c => (c.fits ? c.fits(count) : (count + VAL(c.rank !== undefined ? c.rank : c.r) <= 31)));

      if (myTurn && meCan) {
        const c = CribbageAI.fastPlayCard(myHand, pile, count, settings);
        if (!c) break;
        pile.push(c);
        count += VAL(c.rank !== undefined ? c.rank : c.r);
        const idx = myHand.findIndex(x => x.id === c.id);
        if (idx >= 0) myHand.splice(idx, 1);
        myPts += CribbageScorer.pegTotal(pile, settings);
        lastWasMe = true;
        myTurn = false;
        continue;
      }

      if (!myTurn && oppCan) {
        const c = CribbageAI.fastPlayCard(oppHand, pile, count, settings);
        if (!c) break;
        pile.push(c);
        count += VAL(c.rank !== undefined ? c.rank : c.r);
        const idx = oppHand.findIndex(x => x.id === c.id);
        if (idx >= 0) oppHand.splice(idx, 1);
        oppPts += CribbageScorer.pegTotal(pile, settings);
        lastWasMe = false;
        myTurn = true;
        continue;
      }

      if (myTurn && !meCan && oppCan) { myTurn = false; continue; }
      if (!myTurn && !oppCan && meCan) { myTurn = true; continue; }

      if (count !== 31) {
        if (lastWasMe) myPts += 1;
        else oppPts += 1;
      }
      if (!myHand.length && !oppHand.length) break;
      pile = [];
      count = 0;
      myTurn = !lastWasMe;
      if (myTurn && !myHand.length) myTurn = false;
      if (!myTurn && !oppHand.length) myTurn = true;
    }
    return { myPts, oppPts };
  }

  /**
   * Bonus weighting when nearing match target.
   */
  static raceBonus(whoPts, add, lowball = false, target = 121, oppScore = 0) {
    const mine = (whoPts || 0) + add;
    const opp = oppScore || 0;
    if (!lowball) {
      if (mine >= target && opp < target) return 80;
      if (mine >= target && opp >= target && mine >= opp) return 40;
      if (target - mine <= 4 && add > 0) return 3;
    } else {
      if (mine >= target && (whoPts || 0) < target) return -80;
    }
    return 0;
  }

  /**
   * Chooses optimal card to play during pegging phase.
   */
  static choosePlay(hand, pile = [], count = 0, oppCanPlay = true, lowball = false, options = {}) {
    const rawProf = options.profile || options.aiProfile || 'shark';
    const profile = CribbageAI.normalizeProfile(rawProf);
    const settings = options.settings || {};
    const legal = [];

    for (const c of hand) {
      const isW = c && (c.isWild || c.wild);
      const r = (c && (c.rank !== undefined ? c.rank : c.r)) || 0;
      if (isW) {
        for (const rank of RANKS) {
          if (count + VAL(rank) <= 31) {
            legal.push(Card.from ? Card.from(c).clone().assignWild(rank, 'S') : { ...c, r: rank, playAs: { r: rank, s: 'S' } });
          }
        }
      } else if (count + VAL(r) <= 31) {
        legal.push(c);
      }
    }
    if (!legal.length) return null;

    const usedIds = new Set(options.knownIds || []);
    const fullDeck = options.deck || Deck.makeDeck(options.decks || 1, false);
    const unseen = fullDeck.filter(c => !usedIds.has(c.id));
    const oppN = options.oppHandCount || 4;
    const samples = Math.min(50, Math.max(24, 6 * legal.length));
    const oppWorlds = [];

    for (let t = 0; t < samples; t++) {
      oppWorlds.push(Deck.shuffleArray(unseen.slice()).slice(0, oppN));
    }

    const myScore = options.myScore || 0;
    const oppScore = options.oppScore || 0;
    const target = options.target || 121;
    const ranked = [];

    for (const c of legal) {
      const r = (c && (c.rank !== undefined ? c.rank : c.r)) || 0;
      const newPile = pile.concat([c]);
      const myPts = CribbageScorer.pegTotal(newPile, settings);
      const newCount = count + VAL(r);
      const newHand = hand.filter(x => x.id !== c.id);
      let net = 0;

      for (let t = 0; t < samples; t++) {
        const sim = CribbageAI.rolloutPeg(newHand, oppWorlds[t], newPile, newCount, false, true, settings);
        net += myPts + sim.myPts - sim.oppPts;
      }
      let v = net / samples;
      v += CribbageAI.raceBonus(myScore, myPts, lowball, target, oppScore);

      // Persona modifiers across friendly skill levels
      if (profile === 'gambler') {
        if (myPts >= 2) v += 2.5;
        if (pile.length === 0 && (r === 5 || VAL(r) === 10)) v += 0.8;
      } else if (profile === 'regular') {
        if (myPts >= 2) v += 1.5;
        if (pile.length === 0 && r === 5) v -= 1.5;
      } else if (profile === 'turtler') {
        if (newCount === 15 || newCount === 21 || newCount === 26) v -= 3.0;
        if (pile.length === 0 && r === 5) v -= 4.0;
        if (newCount === 31) v += (settings && settings.opt31sFor5) ? 6.5 : 3.5;
      } else if (profile === 'maverick') {
        if (myPts >= 2) v += 2.2;
        if (newCount === 31) v += 3.8;
        if (pile.length === 0 && (r === 4 || r === 6)) v += 0.8;
      } else if (profile === 'rookie' || profile === 'casual') {
        if (myPts >= 2) v += 3.0;
        if (newCount === 31) v += 2.0;
      } else if (profile === 'master') {
        if (r === 5 && myPts === 0 && newHand.length) v -= 0.6;
        if (newCount === 21 && myPts === 0) v -= 0.8;
        if (newCount === 31) v += (settings && settings.opt31sFor5) ? 5.5 : 3.0;
      } else {
        // Shark (Expert)
        if (r === 5 && myPts === 0 && newHand.length) v -= 0.35;
        if (newCount === 21 && myPts === 0) v -= 0.5;
      }

      const raw = v;
      if (lowball) v = -v;
      ranked.push({ card: c, v, raw, now: myPts });
    }

    ranked.sort((a, b) => b.v - a.v);
    return ranked[0] ? ranked[0].card : null;
  }

  /**
   * Analyzes keep EV for Coach Mode.
   */
  static analyzeKeepEV(keep, deadIds, starter = null, deck = null) {
    if (starter) {
      const pts = CribbageScorer.scoreHand(keep, starter, false, { verbose: false }).total;
      return { min: pts, max: pts, avg: pts };
    }
    const pool = deck || Deck.makeDeck(1, false);
    const deadSet = deadIds instanceof Set ? deadIds : new Set(deadIds || []);
    let minPts = Infinity;
    let maxPts = -Infinity;
    let totalPts = 0;
    let count = 0;

    for (const s of pool) {
      if (deadSet.has(s.id)) continue;
      if (keep.some(c => c.id === s.id)) continue;

      const pts = CribbageScorer.scoreHand(keep, s, false, { verbose: false }).total;
      if (pts < minPts) minPts = pts;
      if (pts > maxPts) maxPts = pts;
      totalPts += pts;
      count++;
    }

    if (count === 0) return { min: 0, max: 0, avg: 0 };
    return {
      min: minPts,
      max: maxPts,
      avg: Number((totalPts / count).toFixed(1))
    };
  }

  /**
   * Monte Carlo guessing for Stolen Crib variant.
   */
  static guessStolenCrib(myDiscards = [], starter = null, myKept = [], deck = null) {
    if (!starter || myDiscards.length < 2) return 4;
    const knownIds = new Set([starter.id, ...myDiscards.map(c => c.id), ...myKept.map(c => c.id)]);
    const pool = deck || Deck.makeDeck(1, false);
    const unseen = pool.filter(c => !knownIds.has(c.id));

    const freq = {};
    const samples = Math.min(36, Math.floor(unseen.length / 2));
    for (let i = 0; i < samples; i++) {
      const shuffled = Deck.shuffleArray(unseen.slice());
      const oppC1 = shuffled[0];
      const oppC2 = shuffled[1];
      if (!oppC1 || !oppC2) continue;
      const hypCrib = [...myDiscards, oppC1, oppC2];
      const pts = CribbageScorer.scoreHand(hypCrib, starter, true, { verbose: false }).total;
      freq[pts] = (freq[pts] || 0) + 1;
    }

    let bestScore = 4;
    let maxCount = -1;
    for (const [pts, count] of Object.entries(freq)) {
      if (count > maxCount) {
        maxCount = count;
        bestScore = parseInt(pts, 10);
      }
    }
    return bestScore;
  }

  /**
   * Blind/random card picker from opponent hand (e.g. for Pick Your Crib).
   */
  static pickBlindFromHand(hand, pickN = 1) {
    if (!hand || hand.length <= pickN) return (hand || []).slice();
    const shuffled = Deck.shuffleArray(hand.slice());
    return shuffled.slice(0, pickN);
  }

  /**
   * Evaluates and assigns ranks/suits for unassigned jokers in CPU hand to optimize score.
   * @param {Array} hand
   * @param {Object} [context]
   */
  static cpuAssignWilds(hand, context = {}) {
    const isWild = context.isWild || (typeof window !== 'undefined' ? window.isWild : (c) => Boolean(c && c.wild));
    const settings = context.settings || (typeof window !== 'undefined' ? window.settings : {});
    const scoreShow = context.scoreShow || (typeof window !== 'undefined' ? window.scoreShow : () => ({ total: 0 }));
    const assignCard = context.assignCard || (typeof window !== 'undefined' ? window.assignCard : (c, r, s) => ({ ...c, r, s, playAs: { r, s } }));
    const log = context.log || (typeof window !== 'undefined' ? window.log : () => {});
    const RANK_NAME = context.RANK_NAME || (typeof window !== 'undefined' ? window.RANK_NAME : { 1: 'A', 2: '2', 3: '3', 4: '4', 5: '5', 6: '6', 7: '7', 8: '8', 9: '9', 10: '10', 11: 'J', 12: 'Q', 13: 'K' });
    const SUIT_SYM = context.SUIT_SYM || (typeof window !== 'undefined' ? window.SUIT_SYM : { S: '♠', H: '♥', C: '♣', D: '♦' });
    const RANKS = context.RANKS || (typeof window !== 'undefined' ? window.RANKS : [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13]);

    const wilds = (hand || []).filter(c => isWild(c) && !c.playAs);
    if (!wilds.length) return;
    const fixed = hand.filter(c => !isWild(c) || c.playAs);
    const suitGuess = (fixed[0] && fixed[0].s) || 'S';
    const trySet = (list) => {
      let best = null, bestV = settings.goal === 'low' ? Infinity : -Infinity;
      const rec = (i) => {
        if (i === list.length) {
          const v = scoreShow(hand.map(c => c.playAs ? assignCard(c, c.r, c.s) : c), null, false, false).total;
          if (settings.goal === 'low' ? v < bestV : v > bestV) { bestV = v; best = list.map(c => ({ id: c.id, r: c.r, s: c.s })); }
          return;
        }
        const c = list[i];
        for (const r of RANKS) {
          c.r = r; c.s = suitGuess; c.playAs = { r, s: suitGuess };
          rec(i + 1);
        }
      };
      rec(0);
      if (best) {
        for (const a of best) {
          const c = hand.find(x => x.id === a.id);
          if (c) { c.r = a.r; c.s = a.s; c.playAs = { r: a.r, s: a.s }; }
        }
      }
    };
    trySet(wilds);
    for (const c of wilds) {
      if (!c.playAs) { c.r = 5; c.s = suitGuess; c.playAs = { r: 5, s: suitGuess }; }
      log('Computer sets joker to ' + (RANK_NAME[c.r] || c.r) + (SUIT_SYM[c.s] || c.s));
    }
  }

  /**
   * Discovers card IDs already known/observed by CPU.
   * @param {Object} [state]
   * @returns {Set<string>}
   */
  static cpuKnownIds(state = null) {
    const s = state || (typeof window !== 'undefined' ? window.state : null);
    const used = new Set();
    const mark = (arr) => { if (arr) for (const c of arr) if (c && c.id) used.add(c.id); };
    mark(s && s.cpuHand);
    mark(s && s.pile);
    if (s && s.starter && s.starter.id) used.add(s.starter.id);
    if (s && s.cpuKept && s.cpuOrig) {
      const kept = new Set(s.cpuKept.map(c => c.id));
      for (const c of s.cpuOrig) if (!kept.has(c.id)) used.add(c.id);
    }
    return used;
  }

  /**
   * Returns unseen cards from fullDeck excluding those known to CPU.
   * @param {Object} [state]
   * @param {Array} [fullDeck]
   * @returns {Array}
   */
  static unseenCards(state = null, fullDeck = null) {
    const deck = fullDeck || (typeof window !== 'undefined' && window.FULL_DECK ? window.FULL_DECK : []);
    const used = CribbageAI.cpuKnownIds(state);
    return deck.filter(c => !used.has(c.id));
  }

  /**
   * Randomly samples opponent hand from unseen cards.
   * @param {Array} unseen
   * @param {number} n
   * @param {Function} [shuffleFn]
   * @returns {Array}
   */
  static sampleOppHand(unseen, n, shuffleFn = null) {
    if (!unseen || n <= 0) return [];
    if (n >= unseen.length) return unseen.slice();
    const copy = unseen.slice();
    const shuf = shuffleFn || (typeof window !== 'undefined' && window.shuffle ? window.shuffle : (arr) => {
      for (let i = arr.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [arr[i], arr[j]] = [arr[j], arr[i]];
      }
      return arr;
    });
    shuf(copy);
    return copy.slice(0, n);
  }

  /**
   * In-place card removal helper.
   * @param {Array} hand
   * @param {Object} card
   */
  static removeCard(hand, card) {
    if (!hand || !card) return;
    const i = hand.findIndex(x => x.id === card.id);
    if (i >= 0) hand.splice(i, 1);
  }

  /**
   * Finds the next player in rotational order who has a legal card that fits under 31.
   * @param {string} fromSeat
   * @param {number} count Current pegging count
   * @param {Array<string>} seats Active seat IDs in rotation order
   * @param {Object} hands Map of seatId -> Card[]
   * @returns {string|null} Seat ID of next player with a play, or null if all pass
   */
  static findNextPlayerWithPlay(fromSeat, count, seats = [], hands = {}) {
    if (!Array.isArray(seats) || seats.length === 0) return null;
    const startIndex = seats.indexOf(fromSeat);
    const n = seats.length;
    for (let i = 1; i <= n; i++) {
      const nextIndex = (startIndex + i) % n;
      const sId = seats[nextIndex];
      const hand = hands[sId] || [];
      const hasFit = hand.some(c => Card.from(c).fits(count));
      if (hasFit) return sId;
    }
    return null;
  }

  /**
   * Evaluates and selects 0 to 5 cards to discard & replace in Draw Cribbage Phase 1 (from 6 cards).
   * @param {Card[]} hand Player's 6-card hand
   * @param {boolean} isDealer Whether player is dealer
   * @param {boolean} [lowball=false]
   * @param {Object} [options={}]
   * @returns {Card[]} Array of cards to discard (empty array if standing pat)
   */
  static chooseDrawPhase1(hand = [], isDealer = false, lowball = false, options = {}) {
    if (!hand || hand.length <= 1) return [];
    const pool = (options.deck || Deck.makeDeck(1, false)).filter(c => !hand.some(h => h.id === c.id));

    const eval6Hand = (sixCards) => {
      let best = lowball ? Infinity : -Infinity;
      const fours = combinations(sixCards, 4);
      for (const four of fours) {
        const sc = CribbageScorer.scoreHand(four, null, false, { verbose: false });
        const val = sc ? sc.total : 0;
        if (lowball ? val < best : val > best) best = val;
      }
      return best;
    };

    let bestDiscard = [];
    let bestScore = eval6Hand(hand);

    for (let k = 1; k <= 5; k++) {
      const discCombos = combinations(hand, k);
      for (const disc of discCombos) {
        const discIds = new Set(disc.map(c => c.id));
        const keep = hand.filter(c => !discIds.has(c.id));
        const sampleCount = 6;
        let tot = 0;
        for (let s = 0; s < sampleCount; s++) {
          const drawn = [];
          const avail = pool.slice();
          for (let d = 0; d < k; d++) {
            const idx = Math.floor(Math.random() * avail.length);
            drawn.push(avail.splice(idx, 1)[0]);
          }
          tot += eval6Hand(keep.concat(drawn));
        }
        const avg = tot / sampleCount;
        if (lowball ? avg < bestScore : avg > bestScore) {
          bestScore = avg;
          bestDiscard = disc;
        }
      }
    }
    return bestDiscard;
  }

  /**
   * Evaluates and selects 0 to 3 cards to discard & replace in Draw Cribbage Phase 2 (from 4 cards).
   * @param {Card[]} hand Player's 4-card hand
   * @param {boolean} isDealer Whether player is dealer
   * @param {boolean} [lowball=false]
   * @param {Card} [starter=null]
   * @param {Object} [options={}]
   * @returns {Card[]} Array of cards to discard (empty array if standing pat)
   */
  static chooseDrawPhase2(hand = [], isDealer = false, lowball = false, starter = null, options = {}) {
    if (!hand || hand.length <= 1) return [];
    const pool = (options.deck || Deck.makeDeck(1, false)).filter(c => !hand.some(h => h.id === c.id));

    const eval4Hand = (fourCards) => {
      const sc = CribbageScorer.scoreHand(fourCards, starter, false, { verbose: false });
      return sc ? sc.total : 0;
    };

    let bestDiscard = [];
    let bestScore = eval4Hand(hand);

    for (let k = 1; k <= 3; k++) {
      const discCombos = combinations(hand, k);
      for (const disc of discCombos) {
        const discIds = new Set(disc.map(c => c.id));
        const keep = hand.filter(c => !discIds.has(c.id));
        const sampleCount = 12;
        let tot = 0;
        for (let s = 0; s < sampleCount; s++) {
          const drawn = [];
          const avail = pool.slice();
          for (let d = 0; d < k; d++) {
            const idx = Math.floor(Math.random() * avail.length);
            drawn.push(avail.splice(idx, 1)[0]);
          }
          tot += eval4Hand(keep.concat(drawn));
        }
        const avg = tot / sampleCount;
        if (lowball ? avg < bestScore : avg > bestScore) {
          bestScore = avg;
          bestDiscard = disc;
        }
      }
    }
    return bestDiscard;
  }

  /**
   * Canadian Doubles: Optimally partitions 10 cards into:
   * - 4 cards kept in hand
   * - 4 cards passed to partner
   * - 2 cards discarded to crib
   * @param {Card[]} cards - 10 dealt cards
   * @param {boolean} [isDealer=false] - whether player belongs to dealer team (crib scores for them)
   * @param {Object} [options={}] - game settings and options
   * @returns {{ keepHand: Card[], partnerHand: Card[], cribDiscards: Card[], score1: number, score2: number, cribScore: number, totalEV: number }}
   */
  static organizeCanadianDoubles(cards, isDealer = false, options = {}) {
    if (!cards || cards.length < 10) {
      const h = (cards || []).slice();
      return {
        keepHand: h.slice(0, 4),
        partnerHand: h.slice(4, 8),
        cribDiscards: h.slice(8, 10),
        score1: 0,
        score2: 0,
        cribScore: 0,
        totalEV: 0
      };
    }

    const starter = options.starter || null;
    const scoreOpts = { verbose: false, ...options.settings };
    const lowball = Boolean(options.settings && options.settings.goal === 'low');
    const isFivesCribbage = Boolean(options.optFivesCribbage || (options.settings && options.settings.optFivesCribbage));
    const isSevensAndEightsCribbage = Boolean(options.optSevensAndEightsCribbage || (options.settings && options.settings.optSevensAndEightsCribbage));

    const cribCombos = combinations(cards, 2);
    let validCribCombos = cribCombos;

    if (isFivesCribbage) {
      const fivesInHand = cards.filter(c => ((c.rank !== undefined ? c.rank : c.r) || 0) === 5);
      const reqFives = Math.min(fivesInHand.length, 2);
      if (reqFives > 0) {
        const filtered = cribCombos.filter(disc => disc.filter(c => ((c.rank !== undefined ? c.rank : c.r) || 0) === 5).length >= reqFives);
        if (filtered.length > 0) validCribCombos = filtered;
      }
    } else if (isSevensAndEightsCribbage) {
      const targetInHand = cards.filter(c => {
        const r = ((c.rank !== undefined ? c.rank : c.r) || 0);
        return r === 7 || r === 8;
      });
      const reqTarget = Math.min(targetInHand.length, 2);
      if (reqTarget > 0) {
        const filtered = cribCombos.filter(disc => disc.filter(c => {
          const r = ((c.rank !== undefined ? c.rank : c.r) || 0);
          return r === 7 || r === 8;
        }).length >= reqTarget);
        if (filtered.length > 0) validCribCombos = filtered;
      }
    }

    let best = null;
    let bestVal = lowball ? Infinity : -Infinity;

    for (const crib of validCribCombos) {
      const cids = new Set(crib.map(c => c.id));
      const rem = cards.filter(c => !cids.has(c.id));
      const handCombos = combinations(rem, 4);
      const halfCount = handCombos.length / 2;

      // Crib heuristic
      const cribBare = CribbageScorer.scoreHand(crib, starter, true, scoreOpts)?.total || 0;
      let cribHeuristic = cribBare * 0.75;
      const r1 = (crib[0].rank !== undefined ? crib[0].rank : crib[0].r) || 0;
      const r2 = (crib[1].rank !== undefined ? crib[1].rank : crib[1].r) || 0;
      const s1 = crib[0].suit || crib[0].s;
      const s2 = crib[1].suit || crib[1].s;
      if (r1 === 5 || r2 === 5) cribHeuristic += 1.5;
      if (VAL(r1) + VAL(r2) === 15) cribHeuristic += 2.0;
      if (r1 === r2) cribHeuristic += 2.0;
      if (Math.abs(r1 - r2) === 1) cribHeuristic += 1.0;
      if (s1 === s2) cribHeuristic += 0.5;

      for (let i = 0; i < halfCount; i++) {
        const h1 = handCombos[i];
        const h1ids = new Set(h1.map(c => c.id));
        const h2 = rem.filter(c => !h1ids.has(c.id));

        const sc1 = CribbageScorer.scoreHand(h1, starter, false, scoreOpts)?.total || 0;
        const sc2 = CribbageScorer.scoreHand(h2, starter, false, scoreOpts)?.total || 0;

        let evalVal = sc1 + sc2;
        if (isDealer) {
          evalVal += cribHeuristic;
        } else {
          evalVal -= cribHeuristic;
        }

        const isBetter = lowball ? (evalVal < bestVal) : (evalVal > bestVal);
        if (isBetter || best === null) {
          bestVal = evalVal;
          const [keep, partner] = sc1 >= sc2 ? [h1, h2] : [h2, h1];
          const [kScore, pScore] = sc1 >= sc2 ? [sc1, sc2] : [sc2, sc1];
          best = {
            keepHand: keep,
            partnerHand: partner,
            cribDiscards: crib,
            score1: kScore,
            score2: pScore,
            cribScore: cribBare,
            totalEV: evalVal
          };
        }
      }
    }

    return best || {
      keepHand: cards.slice(0, 4),
      partnerHand: cards.slice(4, 8),
      cribDiscards: cards.slice(8, 10),
      score1: 0,
      score2: 0,
      cribScore: 0,
      totalEV: 0
    };
  }
}

